/**
 * useFocusTrap — React hook wrapping the focus-trap library (ADR-095
 * Phase 2.4 + §3 stack decisions).
 *
 * Locks keyboard focus inside the referenced element while `active`,
 * deactivates + restores focus on unmount or active=false. Used by the
 * 4 modal/drawer components (JobDetailDrawer, SettingsPanel,
 * CommandPalette, ArtifactPanel) to close the WCAG 2.1 SC 2.1.2 gap
 * flagged in the 2026-05-31 chat-ui audit.
 *
 * Stack decision per ADR-095 §3: focus-trap@^7 raw lib (3 kB gzipped)
 * over the heavier focus-trap-react wrapper. This hook IS the ~10-LOC
 * React wrapper the ADR rationale referenced.
 *
 * Options NOT exposed to callers (set internally) per modal pattern:
 *   - escapeDeactivates: false  — modals own their Escape handler
 *     via modalStack.isTopModal(), so focus-trap should not also fire
 *     close-on-Escape (would double-fire)
 *   - clickOutsideDeactivates: false  — backdrop-click handling lives
 *     in the modal component, same reasoning
 *   - returnFocusOnDeactivate: true  — restore focus to the trigger
 *     button (e.g., Settings gear) when the modal closes
 */

import { createFocusTrap, type FocusTrap } from "focus-trap";
import type { RefObject } from "react";
import { useEffect } from "react";

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;

    let trap: FocusTrap | null = null;
    try {
      trap = createFocusTrap(el, {
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        // Without this, focus-trap's document-level capture-phase click
        // listener calls preventDefault + stopImmediatePropagation on every
        // click outside the trap, which silently kills backdrop-click-to-
        // close across every modal using this hook. Setting this to true
        // lets outside clicks reach their onClick handlers (e.g., the
        // backdrop's setActive(null)) — focus management still works
        // because the focusin listener steals focus back if it tries to
        // escape the trap.
        allowOutsideClick: true,
        returnFocusOnDeactivate: true,
        initialFocus: false,
        fallbackFocus: el,
      });
      trap.activate();
    } catch {
      /* focus-trap may throw if the element has no tabbable descendants;
       * graceful no-op (keyboard nav still works via existing handlers). */
    }

    return () => {
      try {
        trap?.deactivate();
      } catch {
        /* deactivate may throw if already deactivated; ignore */
      }
    };
  }, [ref, active]);
}
